import React, { useEffect, useState } from 'react';

interface ProgressIndicatorProps {
  currentJob: {
    jobId: string;
    status: 'queued' | 'claimed' | 'processing' | 'completed' | 'failed'
      | 'pending' | 'running'; // legacy vocabulary from v1 agents
    progress: number;
    stage: string;
    message: string;
    currentStep?: number;
    totalSteps?: number;
    queueDepth?: number;
    startedAt?: number;
  } | null;
}

/**
 * Humanises a phase name from the LocalAgent taxonomy. Unknown phases
 * fall back to a title-cased version with hyphens stripped — keeps any
 * legacy job entities readable while a job is in flight across an upgrade.
 */
const PHASE_LABELS: Record<string, string> = {
  queued:             'Queued',
  claimed:            'Starting…',
  preparing:          'Preparing',
  'fetching-inputs':  'Fetching inputs',
  'loading-models':   'Loading models',
  generating:         'Generating',
  decoding:           'Decoding',
  encoding:           'Encoding',
  uploading:          'Uploading',
  finalizing:         'Finalizing',
  completed:          'Completed',
  failed:             'Failed',
};

export const humanisePhase = (stage: string): string => {
  if (PHASE_LABELS[stage]) return PHASE_LABELS[stage];
  // Fallback for legacy stages (initializing, generating-image, etc.)
  return stage
    .split('-')
    .map(s => s.length === 0 ? s : s[0].toUpperCase() + s.slice(1))
    .join(' ');
};

/** Format ms as MM:SS for the elapsed-time clock. */
const formatElapsed = (ms: number): string => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const mm = Math.floor(total / 60).toString().padStart(2, '0');
  const ss = (total % 60).toString().padStart(2, '0');
  return `${mm}:${ss}`;
};

/**
 * Legacy sub-progress fallback. v1 agents encoded `value/max` in the
 * `message` column during the `generating` phase; v2 agents send structured
 * `currentStep`/`totalSteps` fields instead (preferred below). Keep the
 * parser so in-flight jobs from a v1 agent still render step counts.
 */
export const parseSubProgress = (message: string): { value: number; max: number } | null => {
  const m = /^\s*(\d+)\s*\/\s*(\d+)\s*$/.exec(message || '');
  if (!m) return null;
  const value = Number(m[1]);
  const max = Number(m[2]);
  if (!Number.isFinite(value) || !Number.isFinite(max) || max <= 0) return null;
  return { value, max };
};

/** Structured steps first (v2 agents), message-parse fallback (v1 agents). */
const getSubProgress = (job: NonNullable<ProgressIndicatorProps['currentJob']>): { value: number; max: number } | null => {
  if (job.currentStep !== undefined && job.totalSteps !== undefined && job.totalSteps > 0) {
    return { value: job.currentStep, max: job.totalSteps };
  }
  return job.stage === 'generating' ? parseSubProgress(job.message) : null;
};

const ProgressIndicator: React.FC<ProgressIndicatorProps> = ({ currentJob }) => {
  // Tick every second so the elapsed clock advances even when the polling
  // loop is between requests. Cheap — only the clock and label re-render.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!currentJob || currentJob.status === 'completed' || currentJob.status === 'failed') return;
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [currentJob]);

  if (!currentJob) {
    return (
      <div className="text-center md:text-left">
        <p className="text-sm text-gray-400">Ready to generate</p>
      </div>
    );
  }

  const barColor =
    currentJob.status === 'completed' ? 'bg-success'   :
    currentJob.status === 'failed'    ? 'bg-error'     :
    (currentJob.status === 'queued' || currentJob.status === 'pending') ? 'bg-warning' :
                                        'bg-blue-500';

  const phaseLabel = humanisePhase(currentJob.stage);
  const sub = getSubProgress(currentJob);
  const queueTail = currentJob.status === 'queued' && (currentJob.queueDepth || 0) > 1
    ? ` · ${(currentJob.queueDepth || 0) - 1} ahead in queue`
    : '';
  const labelText = sub
    ? `${phaseLabel} · step ${sub.value} / ${sub.max}`
    : `${phaseLabel}${queueTail}`;

  const elapsed = currentJob.startedAt ? Date.now() - currentJob.startedAt : 0;
  const showClock = !!currentJob.startedAt && currentJob.status !== 'completed';

  return (
    <div className="w-full rounded-lg md:rounded-none bg-surface-subtle/80 md:bg-transparent px-3 py-2 md:p-0">
      {/* Progress Bar */}
      <div className="mb-2">
        <div className="h-2.5 md:h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full ${barColor} transition-all duration-300 ease-out`}
            style={{ width: `${currentJob.progress}%` }}
          />
        </div>
      </div>

      {/* Single-line status: phase (+ optional sub-progress) on the left,
          elapsed clock and percentage on the right. */}
      <div className="flex items-center justify-between gap-2">
        <span className="text-label font-medium text-gray-700 truncate" title={labelText}>
          {labelText}
        </span>
        <div className="flex items-center gap-2 shrink-0">
          {showClock && (
            <span className="text-meta text-gray-400 tabular-nums" title="Elapsed time">
              {formatElapsed(elapsed)}
            </span>
          )}
          <span className="text-label font-semibold text-blue-500 tabular-nums">
            {currentJob.progress}%
          </span>
        </div>
      </div>
      {currentJob.message && !sub && (
        <p className="mt-1 text-meta text-gray-400 truncate" title={currentJob.message}>
          {currentJob.message}
        </p>
      )}
    </div>
  );
};

export default ProgressIndicator;

